/**
 * Custom TipTap Embed (iframe) node extension.
 * Supports YouTube, Vimeo, and generic embed URLs.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { mediaLayoutAttributes } from './tiptap-media-layout';

export interface EmbedAttributes {
    src: string;
    width?: number | string | null;
    height?: number | null;
    title?: string | null;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        embed: {
            /** Insert an embed (iframe) block node. */
            setEmbed: (attrs: EmbedAttributes) => ReturnType;
        };
    }
}

/** Convert common video URLs to their embeddable form. */
export function toEmbedUrl(url: string): string {
    const trimmed = url.trim();

    // YouTube: watch?v=ID / youtu.be/ID / shorts/ID
    const ytWatch = trimmed.match(
        /(?:youtube\.com\/watch\?.*v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{11})/
    );
    if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;

    // Already a YouTube embed URL
    if (/youtube\.com\/embed\//.test(trimmed)) return trimmed;

    // Vimeo: vimeo.com/ID
    const vimeo = trimmed.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

    // Already a Vimeo embed URL
    if (/player\.vimeo\.com\/video\//.test(trimmed)) return trimmed;

    return trimmed;
}

export const Embed = Node.create({
    name: 'embed',
    // EAA-1736: inline-level so embeds can flow with text (inline layout).
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            src: { default: null },
            width: { default: null },
            height: { default: 315 },
            title: { default: null },
            // EAA-1736: shared layout attrs (data-layout / data-x / data-y).
            ...mediaLayoutAttributes(),
        };
    },

    parseHTML() {
        return [
            {
                tag: 'iframe[src]',
                getAttrs: (node) => {
                    const el = node as HTMLIFrameElement;
                    return {
                        src: el.getAttribute('src'),
                        width: el.getAttribute('width') || null,
                        height: el.getAttribute('height') || 315,
                        title: el.getAttribute('title') || null,
                    };
                },
            },
            {
                tag: 'div[data-embed-src]',
                getAttrs: (node) => {
                    const el = node as HTMLElement;
                    return {
                        src: el.getAttribute('data-embed-src'),
                        width: el.getAttribute('data-embed-width') || null,
                        height: el.getAttribute('data-embed-height') || 315,
                        title: el.getAttribute('data-embed-title') || null,
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const attrs = HTMLAttributes as EmbedAttributes & Record<string, unknown>;
        const { src, width, height, title } = attrs;
        // EAA-1736: forward the layout data-* attrs so the static viewer can lay
        // the iframe out (wrap / behind / front) via the shared CSS.
        const layoutAttrs: Record<string, unknown> = {};
        if (attrs['data-layout'] != null) layoutAttrs['data-layout'] = attrs['data-layout'];
        if (attrs['data-x'] != null) layoutAttrs['data-x'] = attrs['data-x'];
        if (attrs['data-y'] != null) layoutAttrs['data-y'] = attrs['data-y'];
        // Forward the overlay position style (behind/front) for the viewer.
        if (attrs['style'] != null) layoutAttrs['style'] = attrs['style'];
        return [
            'iframe',
            mergeAttributes(
                {
                    src,
                    width: width ?? '100%',
                    height: height ?? 315,
                    title: title ?? 'Embedded content',
                    frameborder: '0',
                    allowfullscreen: 'true',
                    allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen',
                    sandbox: 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation allow-forms',
                    referrerpolicy: 'no-referrer-when-downgrade',
                    loading: 'lazy',
                    class: 'tiptap-embed-iframe',
                    ...layoutAttrs,
                },
            ),
        ];
    },

    addCommands() {
        return {
            setEmbed:
                (attrs) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: { ...attrs, src: toEmbedUrl(attrs.src) },
                    });
                },
        };
    },
});

export default Embed;
